import { useEffect, useMemo, useRef, useState } from "react";
import { IconDownload } from "@icons/index";
import VideoPlayer from "../components/VideoPlayer";
import { VideoPlayerRef } from "@layouts/GlobalVideoModal/type";
import { TransitionPanel } from "../components/TransitionPanel";
import { VideoScriptPanel } from "../components/VideoScriptPanel";
import { useVideoScript } from "./hooks";
import Empty from "../components/Empty";
import { useOpenWindow } from "@utils/window";
import "./index.scss";
import { Trans } from "react-i18next";
import { Section } from "@src/ui/Section";
import { PageHeader } from "@layouts/PageHeader";
import { useNavigationGuard } from "@layouts/NavigationGuard";
import { t } from "i18next";
import { message, Tag } from "antd";
import { DownloadVideo } from "@components/DownloadVideo";
import { useMembershipLevel } from "@hooks/membership";
import Insight from "./insight";
import { useAppDispatch } from "@hooks/redux";
import { fetchAiCredits } from "@store/globalSlice";
import classNames from "classnames";
import { useUrlLink } from "./useUrlLink";

export const ScriptExport = () => {
  const refPlayer: any = useRef<VideoPlayerRef>(null);
  const {
    error,
    status: scriptStatus,
    videoScript,
    reload,
    translateStatus,
    showTranslate,
    defaultParam,
    collectionDetail,
    setCollectionDetail,
    videoId,
    videoSource,
    collectionId,
    videoInsight,
    handleVideoInsight,
    resetAll,
  } = useVideoScript();
  const urlLink = useUrlLink();
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [isTrans, setIsTrans] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [checkingCreditsLoading, setCheckingCreditsLoading] = useState(false);
  const { openWindow } = useOpenWindow();
  const { isBasicUp } = useMembershipLevel();
  const dispatch = useAppDispatch();

  const { showLeaveConfirm, GuardModal } = useNavigationGuard(!!videoId, {
    onConfirm: resetAll,
  });

  useEffect(() => {
    if (scriptStatus === "succeeded") {
      setTimeout(() => {
        setShowResult(true);
      }, 1000);
    } else {
      setShowResult(false);
    }
  }, [scriptStatus]);

  const handleSearch = async (inputWorld: string) => {
    if (checkingCreditsLoading || videoInsight.status === "loading" || !inputWorld.trim()) {
      return;
    }
    setCheckingCreditsLoading(true);
    try {
      const res: any = await dispatch(fetchAiCredits());
      const totalRemain = res.payload.totalRemain || 0;
      if (totalRemain < 1) {
        message.error("Not enough credits");
        return;
      }
      handleVideoInsight(inputWorld);
    } catch (error: any) {
      message.error(error?.message || "Failed to check credits");
    } finally {
      setCheckingCreditsLoading(false);
    }
  };

  const scriptText = useMemo(() => {
    const video_scripts: any = [];
    ((videoScript as any)?.video_scripts || []).forEach((item: any) => video_scripts.push(...item.audio_script));
    return video_scripts;
  }, [videoScript]);

  const activeScriptText = useMemo(() => {
    return scriptText.find((item: any) => item.start_time <= currentTime && item.end_time >= currentTime) || {};
  }, [scriptText, currentTime]);

  // 搜索页
  if (!videoId)
    return (
      <div className="ScriptExport h-full relative">
        <Insight
          defaultKeyword={urlLink}
          videoInsight={videoInsight}
          handleSearch={handleSearch}
          loading={checkingCreditsLoading}
        />
      </div>
    );

  // 错误页
  if (!isBasicUp || error) {
    return (
      <Empty
        image="8903ffad.png"
        tips={error?.message || "Sorry, You don't have permission to access this page."}
        label="Back to homepage"
        className="flex-1"
        handleBtnClick={() => {
          openWindow("/");
        }}
      />
    );
  }

  return (
    <div className="ScriptExport h-screen relative">
      <PageHeader
        title={collectionId ? collectionDetail?.data?.name : t("Video to Storyboard")}
        onBack={showLeaveConfirm}
      />
      <div className="flex flex-row items-start h-[calc(100%-53px)]">
        {/* 左侧视频卡片 */}
        <div className="w-[292px] h-[535px] shrink-0 flex flex-col items-center p-[12px]">
          <Section
            status={videoInsight.status}
            error={videoInsight.error}
            className={classNames("w-full h-full flex flex-col items-center", {
              "kalo-white-card border-none!": videoInsight.status === "loading",
            })}
          >
            <VideoPlayer
              key={videoId}
              ref={refPlayer}
              videoURL={videoInsight.data?.url}
              videoId={videoId as string}
              className="relative w-[268px] h-[477px] rounded-[8px] bg-white"
              onTimeUpdate={(e) => setCurrentTime(e)}
              onPause={() => setIsPlaying(false)}
              onPlay={() => setIsPlaying(true)}
              scriptText={isTrans ? activeScriptText.translate_script : activeScriptText.script}
            />
            <DownloadVideo videoId={videoId as string} videoSource={videoSource as string}>
              <div className="download-btn flex items-center gap-[4px] font-medium text-kds-fg text-[14px] mt-[12px] cursor-pointer transition-all">
                <IconDownload className="w-[20px] h-[20px]" />
                <Trans>Download Video</Trans>
                <Tag className="rounded-full!" color="processing">
                  <Trans>No Watermark</Trans>
                </Tag>
              </div>
            </DownloadVideo>
          </Section>
        </div>

        <div className="relative -top-[53px] z-10 h-screen kalo-shadow bg-kds-bg w-full">
          {showResult ? (
            <VideoScriptPanel
              showProduct={false}
              status={scriptStatus}
              data={[{ ...collectionDetail, ...videoScript, ...defaultParam }]}
              scriptText={activeScriptText}
              isPlaying={isPlaying}
              handleCurTime={(e) => refPlayer?.current.setCurrentTime(e)}
              handleTranslate={(e) => {
                setIsTrans(e);
                showTranslate(e);
              }}
              handleSave={setCollectionDetail}
              isTranslateLoading={translateStatus === "loading"}
            />
          ) : (
            <TransitionPanel
              key={refreshKey}
              status={scriptStatus}
              onRetry={() => {
                setRefreshKey(refreshKey + 1);
                reload();
              }}
            />
          )}
        </div>
      </div>
      {GuardModal}
    </div>
  );
};
